import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Select, FormControl, InputLabel, Box, Typography } from '@mui/material';
import axios from 'axios';
import { getBackendUserUrl } from '../../../utils/utils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEventOperationsData } from '../../../contexts/EventContext';

const AddAgendaModal = ({
  open,
  onClose,
  onSave,
  initialData = {},
  eventId,
  isEdit = false,
  tasks = []
}) => {
  const [name, setName] = useState(initialData.name || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [startTime, setStartTime] = useState(initialData.startTime ? dayjs(initialData.startTime, ['HH:mm', 'hh:mm A', 'HH:mm:ss+00']) : null);
  const [endTime, setEndTime] = useState(initialData.endTime ? dayjs(initialData.endTime, ['HH:mm', 'hh:mm A', 'HH:mm:ss+00']) : null);
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [initialGroupId, setInitialGroupId] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [date, setDate] = useState(initialData.date ? dayjs(initialData.date) : dayjs());
  const [timeError, setTimeError] = useState('');
  const [timeTouched, setTimeTouched] = useState(false);
  const { fetchAgendas } = useEventOperationsData();

  // Filter tasks for this agenda
  const agendaTasks = (isEdit && initialData.agendaId)
    ? tasks.filter(task => task.agendaId === initialData.agendaId)
    : [];

  // Helper function to calculate minutes between times
  const getMinutesBetween = (start, end) => {
    if (!start || !end) return 0;
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);
    
    // Handle cases where end time is on the next day
    let hoursDiff = endHours - startHours;
    if (hoursDiff < 0) {
      hoursDiff += 24;  // Add 24 hours if end time is on next day
    }
    
    const minutesDiff = (hoursDiff * 60) + (endMinutes - startMinutes);
    console.log('Time diff calculation:', {
      start,
      end,
      startHours,
      startMinutes,
      endHours,
      endMinutes,
      hoursDiff,
      minutesDiff
    });
    return minutesDiff;
  };

  // Validate times whenever they change
  useEffect(() => {
    if (!timeTouched) return; // Only validate if user has interacted
    if (startTime && endTime) {
      const start = startTime;
      const end = endTime;
      const minutesDiff = getMinutesBetween(
        start.format('HH:mm'),
        end.format('HH:mm')
      );
      if (minutesDiff === 0) {
        setTimeError('Start and end time cannot be the same');
      } else if (minutesDiff < 0) {
        setTimeError('End time must be after start time');
      } else {
        setTimeError('');
      }
    } else {
      setTimeError('');
    }
  }, [startTime, endTime, timeTouched]);

  // Handle start time change
  const handleStartTimeChange = (newValue) => {
    setTimeTouched(true);
    setStartTime(newValue);
  };

  // Handle end time change
  const handleEndTimeChange = (newValue) => {
    setTimeTouched(true);
    setEndTime(newValue);
  };

  useEffect(() => {
    if (open && eventId) {
      setLoadingGroups(true);
      // Fetch assignment groups
      axios.get(`${getBackendUserUrl()}/agenda/assignment_groups/${eventId}`)
        .then(res => setAssignmentGroups(res.data || []))
        .catch(() => setAssignmentGroups([]))
        .finally(() => setLoadingGroups(false));

      // If we have an agendaId, fetch the assignee details
      if (initialData.agendaId) {
        axios.get(`${getBackendUserUrl()}/agenda/agenda_items/${initialData.agendaId}/assignee`)
          .then(response => {
            if (response.data) {
              setSelectedGroup(response.data.assignmentGroupId);
              setInitialGroupId(response.data.assignmentGroupId);
            }
          })
          .catch(error => {
            console.error('Error fetching assignee details:', error);
          });
      } else {
        setSelectedGroup(assignmentGroups[0]?.assignmentGroupId || '');
        setInitialGroupId('');
      }
    }
  }, [open, eventId, initialData.agendaId]);

  useEffect(() => {
    if (open) {
      console.log('Updating form with initialData:', {
        initialData,
        isEdit,
        name: initialData.name,
        startTime: initialData.startTime,
        endTime: initialData.endTime,
        selectedGroup: initialData.assignmentGroupId,
        assignmentGroups
      });
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setStartTime(initialData.startTime ? dayjs(initialData.startTime, ['HH:mm', 'hh:mm A', 'HH:mm:ss+00']) : null);
      setEndTime(initialData.endTime ? dayjs(initialData.endTime, ['HH:mm', 'hh:mm A', 'HH:mm:ss+00']) : null);
      setDate(initialData.date ? dayjs(initialData.date) : dayjs());

      // If we have an agendaId, fetch the assignee details
      if (initialData.agendaId) {
        axios.get(`${getBackendUserUrl()}/agenda/agenda_items/${initialData.agendaId}/assignee`)
          .then(response => {
            if (response.data) {
              setSelectedGroup(response.data.assignmentGroupId);
            }
          })
          .catch(error => {
            console.error('Error fetching assignee details:', error);
          });
      } else {
        setSelectedGroup(assignmentGroups[0]?.assignmentGroupId || '');
      }
    }
  }, [initialData, open, assignmentGroups]);

  // Add debug logging for current form state
  useEffect(() => {
    if (open && isEdit) {
      console.log('Current form state:', {
        name,
        startTime,
        endTime,
        selectedGroup,
        date: date?.format('YYYY-MM-DD'),
        timeError,
        isDisabled: !name.trim() || !startTime || !endTime || !selectedGroup || !date || !!timeError
      });
    }
  }, [name, startTime, endTime, selectedGroup, date, timeError, open, isEdit]);

  // When modal opens, reset timeTouched
  useEffect(() => {
    if (open) setTimeTouched(false);
  }, [open]);

  const handleSave = async () => {
    if (!startTime || !endTime || !date || timeError) {
      console.log('Save validation failed:', {
        startTime: !startTime,
        endTime: !endTime,
        date: !date,
        timeError
      });
      return;
    }

    // Format times to match backend expectation (HH:mm:ss+00)
    const formattedStartTime = `${startTime.format('HH:mm')}:00+00`;
    const formattedEndTime = `${endTime.format('HH:mm')}:00+00`;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      start_time: formattedStartTime,
      end_time: formattedEndTime,
      assignmentGroupId: selectedGroup === 'unassigned' ? '' : selectedGroup,
      date: date.format('YYYY-MM-DD'),
    };

    console.log('Saving agenda with payload:', payload);

    try {
      let agendaResponse;
      
      if (isEdit && initialData.agendaId) {
        // For edit, use PATCH endpoint
        agendaResponse = await axios.patch(
          `${getBackendUserUrl()}/agenda/agenda_items/${initialData.agendaId}`,
          payload
        );

        // If assignment group changed, delete old assignee and create new one
        if (initialGroupId !== selectedGroup) {
          // Delete old assignee
          await axios.delete(
            `${getBackendUserUrl()}/agenda/agenda_assignees/`,
            { params: { agenda_id: initialData.agendaId, assignment_group_id: initialGroupId } }
          );
          
          // Create new assignee
          await axios.post(
            `${getBackendUserUrl()}/agenda/agenda_assignees/`,
            {
              agenda_id: initialData.agendaId,
              assignment_group_id: selectedGroup
            }
          );
        }
      } else {
        // For add, use POST endpoint
        agendaResponse = await axios.post(
          `${getBackendUserUrl()}/agenda/agenda_items/`,
          {
            ...payload,
            event_id: eventId,
            space_id: initialData.spaceId,
            created_by: initialData.userId
          }
        );

        // Create new assignee only if not unassigned
        if (selectedGroup && selectedGroup !== 'unassigned') {
          await axios.post(
            `${getBackendUserUrl()}/agenda/agenda_assignees/`,
            {
              agenda_id: agendaResponse.data.agendaId,
              assignment_group_id: selectedGroup
            }
          );
        }
      }

      onSave(agendaResponse.data);
      fetchAgendas();
    } catch (error) {
      console.error('Failed to save agenda item:', error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Edit Agenda' : 'Add Agenda'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 320 }}>
          <TextField
            label="Title"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            multiline
            rows={3}
          />
          <DatePicker
            label="Date"
            value={date}
            onChange={setDate}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="assign-group-label">Assign To</InputLabel>
            <Select
              labelId="assign-group-label"
              value={selectedGroup || 'unassigned'}
              label="Assign To"
              onChange={e => setSelectedGroup(e.target.value)}
              disabled={loadingGroups}
            >
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {assignmentGroups.map(group => (
                <MenuItem key={group.assignmentGroupId} value={group.assignmentGroupId}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={handleStartTimeChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!timeError,
                    inputProps: {
                      placeholder: 'hh:mm AM/PM',
                    },
                  }
                }}
                views={['hours', 'minutes']}
                ampm={true}
                ampmInClock={true}
                format="hh:mm A"
                allowKeyboardControl={true}
                inputFormat="hh:mm A"
              />
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={handleEndTimeChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!timeError,
                    inputProps: {
                      placeholder: 'hh:mm AM/PM',
                    },
                  }
                }}
                views={['hours', 'minutes']}
                ampm={true}
                ampmInClock={true}
                format="hh:mm A"
                allowKeyboardControl={true}
                inputFormat="hh:mm A"
              />
            </Box>
            {timeError && (
              <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                {timeError}
              </Typography>
            )}
          </Box>
          {/* Add non-editable list of tasks for this agenda */}
          {isEdit && initialData.agendaId && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Tasks for this Agenda
              </Typography>
              {agendaTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tasks assigned to this agenda item.
                </Typography>
              ) : (
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {agendaTasks.map(task => (
                    <li key={task.taskId}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{task.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Status: {task.status}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={!startTime || !endTime || !date || !!timeError}
          >
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddAgendaModal; 